import { Icon } from '@latitude/icons';
import { format as formatHandler } from 'date-fns/format';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isValidDate } from 'utils/dateUtils';
import { selectDayHandler } from 'utils/eventUtils';
import {
  isIncompleteInput,
  validateDateInput,
  validateDateRangeInput,
} from 'utils/validation';

import type {
  DatePickerMode,
  DatePickerValue,
  DatePickerValues,
  DatepickerInputProps,
  DatepickerInputState,
  DatepickerProps,
} from '../types';
import type {
  DatepickerPropsDate,
  DatepickerPropsDateRange,
  DatepickerProviderProps,
} from './HookTypes';

type UseDatepickerHandlersProps = {
  mode: DatepickerProviderProps['mode'];
  minDate: DatepickerProps['minDate'];
  maxDate: DatepickerProps['maxDate'];
  required: DatepickerProviderProps['required'];
  format: Exclude<DatepickerProps['format'], undefined>;

  // state
  open: Exclude<DatepickerProviderProps['controlledOpen'], undefined>;
  setOpen: Exclude<DatepickerProviderProps['controlledSetOpen'], undefined>;
  controlledOnOpenChange?: DatepickerProviderProps['controlledOnOpenChange'];
  tempValue: DatePickerValue<DatePickerMode> | undefined;
  setTempValue: (value: DatePickerValue<DatePickerMode> | undefined) => void;
  rangeDirection: 'from' | 'to';
  setRangeDirection: (direction: 'from' | 'to') => void;
  exportedValue: DatePickerValue<DatePickerMode> | undefined;
  setExportedValue: (
    value: DatePickerValue<DatePickerMode> | undefined,
  ) => void;

  calendarFromDateSelected?: Date;
  calendarOnFromDateChange: (d: Date | undefined) => void;
  calendarToDateSelected?: Date;
  calendarOnToDateChange: (d: Date | undefined) => void;

  // formatting
  formattedValue: string | undefined;

  // handlers
  onChange:
    | ((payload: DatePickerValues<'single' | 'time' | 'single-time'>) => void)
    | ((payload: DatePickerValues<'range'>) => void)
    | undefined;

  disabled?: boolean;
  readOnly?: boolean;
  onClear?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  'data-testid'?: string;
  placeholder?: string;
};

// Helper to format a value for display
const formatValueForDisplay = (
  value: DatePickerValue<DatePickerMode> | undefined,
  mode: DatePickerMode,
  format: string,
): string => {
  if (!value) {
    return '';
  }
  if (mode === 'range') {
    const range = value as { from?: Date; to?: Date };
    if (isValidDate(range.from) && isValidDate(range.to)) {
      return `${formatHandler(range.from, format)} - ${formatHandler(range.to, format)}`;
    }
    if (isValidDate(range.from)) {
      return `${formatHandler(range.from, format)} - `;
    }
    return '';
  }
  return isValidDate(value) ? formatHandler(value, format) : '';
};

export const useDatepickerHandlers = ({
  mode,
  format,
  minDate,
  maxDate,
  setOpen,
  controlledOnOpenChange,
  calendarFromDateSelected: fromDateSelected,
  calendarOnFromDateChange: setFromDateSelected,
  calendarToDateSelected: toDateSelected,
  calendarOnToDateChange: setToDateSelected,
  exportedValue,
  setExportedValue,
  onChange,
  setTempValue,
  rangeDirection,
  setRangeDirection,
  formattedValue: _formattedValue,
  disabled,
  readOnly,
  onClear,
  onBlur,
  placeholder,
  ...rest
}: UseDatepickerHandlersProps) => {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Input state management - separate display value from actual value
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatValueForDisplay(exportedValue, mode, format),
  );
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Track if we're updating from internal source to avoid sync loops
  const isInternalUpdate = useRef(false);

  // Sync display value when exportedValue changes externally (calendar selection, clear)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const formatted = formatValueForDisplay(exportedValue, mode, format);
    setDisplayValue(formatted);
    setHasError(false);
    setErrorMessage(undefined);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [exportedValue]);

  // Cleanup debounce timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  // Sync display value from a programmatic date change
  const syncFromDate = useCallback(
    (value: unknown) => {
      const formatted = formatValueForDisplay(
        value as DatePickerValue<DatePickerMode>,
        mode,
        format,
      );
      setDisplayValue(formatted);
      setHasError(false);
      setErrorMessage(undefined);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [formatValueForDisplay],
  );

  // Clear error state
  const clearError = useCallback(() => {
    setHasError(false);
    setErrorMessage(undefined);
  }, []);

  // Input state object for context
  const inputState: DatepickerInputState = {
    displayValue,
    hasError,
    errorMessage,
    syncFromDate,
    clearError,
  };

  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      // The single choke point for opening the calendar — gating here (rather
      // than only the input's onClick) also covers Radix's own trigger-click
      // handling, which calls this same callback directly.
      if (newOpen && readOnly) {
        return;
      }
      if (newOpen) {
        if (mode === 'range' && fromDateSelected && !toDateSelected) {
          setRangeDirection('to');
        }
        setTempValue(exportedValue);
      }
      setOpen(newOpen);
      controlledOnOpenChange?.(newOpen);
    },
    [
      readOnly,
      setOpen,
      controlledOnOpenChange,
      mode,
      fromDateSelected,
      toDateSelected,
      setTempValue,
      exportedValue,
      setRangeDirection,
    ],
  );

  const handleNewValueChange = useCallback(
    (newDate: DatePickerValue<typeof mode> | undefined) => {
      let newExportedValue = undefined;
      let newFormattedValue = '';
      let newFromDate = undefined;
      let newToDate = undefined;

      if (!newDate) {
        setFromDateSelected(undefined);
        setToDateSelected(undefined);
        onChange?.({
          value: newExportedValue,
          formattedValue: newFormattedValue,
        });
        setExportedValue(newExportedValue);
        return;
      }

      if (mode === 'range') {
        newExportedValue = newDate as Exclude<
          DatePickerValue<'range'>,
          undefined
        >;
        const { from, to } = newExportedValue;

        newFromDate = from;
        newToDate = to || undefined;

        if (from && to) {
          newFormattedValue = `${formatHandler(from, format)} - ${formatHandler(
            to,
            format,
          )}`;
        } else if (from) {
          newFormattedValue = `${formatHandler(from, format)} - `;
        }
        (onChange as DatepickerPropsDateRange['onChange'])?.({
          value: newExportedValue,
          formattedValue: newFormattedValue,
        });
      } else {
        newExportedValue = newDate as Exclude<
          DatePickerValue<'single' | 'time' | 'single-time'>,
          undefined
        >;
        if (!isValidDate(newExportedValue)) {
          return;
        }
        newFromDate = newExportedValue;
        newFormattedValue = formatHandler(newExportedValue, format);
        (onChange as DatepickerPropsDate['onChange'])?.({
          value: newExportedValue,
          formattedValue: newFormattedValue,
        });
      }

      setFromDateSelected(newFromDate);
      setToDateSelected(newToDate);
      setExportedValue(newExportedValue);
    },
    [
      mode,
      format,
      onChange,
      setExportedValue,
      setFromDateSelected,
      setToDateSelected,
    ],
  );

  const onDayClickHandler = useCallback(
    (day: Date) =>
      selectDayHandler(
        day,
        mode,
        fromDateSelected,
        handleNewValueChange,
        rangeDirection,
        setRangeDirection,
        onOpenChange,
      ),
    [
      mode,
      fromDateSelected,
      handleNewValueChange,
      rangeDirection,
      setRangeDirection,
      onOpenChange,
    ],
  );
  const inputProps: DatepickerInputProps = {
    disabled,
    readOnly,
    // Use displayValue instead of derived value - preserves typed input even when invalid
    value: displayValue,
    // Show error state on input
    error: hasError,
    trailingElement: <Icon name="calendar" className="px-[2px]" />,
    classNames: {
      input: 'flex justify-start',
      wrapper: hasError ? 'border-red-500' : '',
    },
    placeholder: placeholder || 'Pick a date',
    autoComplete: 'off',
    'data-testid': rest['data-testid'] || 'datepicker-input',
    'aria-haspopup': 'dialog',
    onClear: () => {
      setDisplayValue('');
      setHasError(false);
      setErrorMessage(undefined);
      handleNewValueChange(undefined);
      onClear?.();
    },
    onClick: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      onOpenChange(true);
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (mode === 'time') {
        return;
      }

      const typedValue = e.target.value;

      // Always update display value immediately - preserves what user typed
      setDisplayValue(typedValue);

      // Clear error while typing (will revalidate on debounce)
      if (hasError) {
        setHasError(false);
        setErrorMessage(undefined);
      }

      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout for validation
      debounceTimeoutRef.current = setTimeout(() => {
        // Empty input - clear value but no error
        if (!typedValue.trim()) {
          isInternalUpdate.current = true;
          handleNewValueChange(undefined);
          return;
        }

        // Check if input is incomplete (user still typing)
        if (isIncompleteInput(typedValue, format)) {
          // Don't show error for incomplete input
          return;
        }

        // Validate the input
        const validationConfig = {
          format,
          minDate,
          maxDate,
          strictMode: false,
        };

        if (mode === 'range') {
          const result = validateDateRangeInput(typedValue, validationConfig);
          if (result.isValid && result.parsedRange) {
            isInternalUpdate.current = true;
            handleNewValueChange(result.parsedRange);
            setHasError(false);
            setErrorMessage(undefined);
          } else if (result.errorType !== 'INCOMPLETE' && result.errorMessage) {
            // Show error for invalid input (not incomplete)
            setHasError(true);
            setErrorMessage(result.errorMessage);
          }
        } else {
          const result = validateDateInput(typedValue, validationConfig);
          if (result.isValid && result.parsedDate) {
            isInternalUpdate.current = true;
            handleNewValueChange(result.parsedDate);
            setHasError(false);
            setErrorMessage(undefined);
            // If input was in alternate format (e.g., 24h time), update display to canonical format
            if (result.correctedValue) {
              setDisplayValue(result.correctedValue);
            }
          } else if (result.errorType !== 'INCOMPLETE' && result.errorMessage) {
            // Show error for invalid input (not incomplete)
            setHasError(true);
            setErrorMessage(result.errorMessage);
          }
        }
      }, 500);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      // Don't propagate blur (local or consumer) when focus moved to the
      // datepicker popover — the interaction is still in progress (e.g.
      // clicking a calendar day blurs the input before its click handler
      // commits the new value). Forwarding to the consumer here would fire
      // a premature react-hook-form validation against the stale value.
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (relatedTarget?.closest('[data-testid="datepicker-popover"]')) {
        return;
      }

      // Clear debounce timer
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      const typedValue = displayValue.trim();

      // Empty input is valid - clear value
      if (!typedValue) {
        if (exportedValue !== undefined) {
          isInternalUpdate.current = true;
          handleNewValueChange(undefined);
        }
        setHasError(false);
        setErrorMessage(undefined);
        onBlur?.(e);
        return;
      }

      // Strict validation on blur
      const validationConfig = { format, minDate, maxDate, strictMode: true };

      if (mode === 'range') {
        const result = validateDateRangeInput(typedValue, validationConfig);
        if (result.isValid && result.parsedRange) {
          isInternalUpdate.current = true;
          handleNewValueChange(result.parsedRange);
          setHasError(false);
          setErrorMessage(undefined);
        } else {
          setHasError(true);
          setErrorMessage(result.errorMessage || 'Invalid date range');
        }
      } else {
        const result = validateDateInput(typedValue, validationConfig);
        if (result.isValid && result.parsedDate) {
          isInternalUpdate.current = true;
          handleNewValueChange(result.parsedDate);
          setHasError(false);
          setErrorMessage(undefined);
          // If input was in alternate format (e.g., 24h time), update display to canonical format
          if (result.correctedValue) {
            setDisplayValue(result.correctedValue);
          }
        } else {
          setHasError(true);
          setErrorMessage(result.errorMessage || 'Invalid date');
        }
      }

      onBlur?.(e);
    },
  };

  return {
    onOpenChange,
    handleNewValueChange,
    onDayClickHandler,
    inputProps,
    inputState,
  };
};
